import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Bell, Search, Trash2, CheckCircle2, ArrowLeft } from "lucide-react";
import { useNotifications } from "../../contexts/NotificationsContext";

export default function Notifications() {
  const navigate = useNavigate();
  const { items, unread, markAllRead, clearAll, openNotification } = useNotifications();

  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = String(q || "").trim().toLowerCase();
    if (!query) return items;

    return items.filter((n) => {
      const title = String(n.title || "").toLowerCase();
      const msg = String(n.message || "").toLowerCase();
      const type = String(n.type || "").toLowerCase();
      const src = String(n.source || "").toLowerCase();
      return (
        title.includes(query) ||
        msg.includes(query) ||
        type.includes(query) ||
        src.includes(query)
      );
    });
  }, [items, q]);

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      <div className="w-full min-w-0 p-4 sm:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  toast("Going back...");
                  navigate(-1);
                }}
                className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-gray-200 dark:border-gray-800
                          bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-700 dark:text-gray-200" />
              </button>

              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Notifications
              </h1>

              {unread > 0 && (
                <span className="ml-1 inline-flex items-center rounded-full bg-red-500 text-white text-xs font-bold px-2 py-0.5">
                  {unread > 99 ? "99+" : unread} unread
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                markAllRead();
                toast.success("All notifications marked as read.");
              }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-gray-200 dark:border-gray-800
                         bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition text-sm font-semibold
                         text-gray-800 dark:text-gray-100"
            >
              <CheckCircle2 className="h-4 w-4" />
              Mark all read
            </button>

            <button
              onClick={() => {
                const ok = window.confirm("Clear all notifications?");
                if (ok) {
                  clearAll();
                  toast.success("All notifications cleared.");
                }
              }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 transition text-sm font-semibold text-white"
            >
              <Trash2 className="h-4 w-4" />
              Clear all
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/40 grid place-items-center">
              <Search className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, message, type..."
              className="w-full h-10 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3
                         text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* List */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-6 text-sm text-gray-600 dark:text-gray-400">
              {items.length === 0 ? "No notifications yet." : "No results found."}
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map((n) => {
                const key = n.dedupeKey || n.id || `${n.type}:${n.createdAt}:${n.title}`;
                const created = n.createdAt ? new Date(n.createdAt).toLocaleString() : "";
                const isUnread = !n.read;

                return (
                  <button
                    key={key}
                    onClick={() => {
                      toast("Opening notification...");
                      openNotification(n, navigate);
                    }}
                    className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${
                            isUnread ? "bg-blue-600" : "bg-transparent"
                          }`}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-semibold truncate ${
                                isUnread
                                  ? "text-gray-900 dark:text-gray-100"
                                  : "text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {n.title || "Notification"}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {n.type || "system"} {n.source ? `• ${n.source}` : ""} {created ? `• ${created}` : ""}
                            </p>
                          </div>

                          <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                            Open →
                          </span>
                        </div>

                        {n.message && (
                          <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
                            {n.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}